import React, { useMemo } from 'react';
import KpiCard from '../components/KpiCard';
import { ClipboardList, Cog, CheckCircle2, XCircle, AlertTriangle, CalendarDays } from 'lucide-react';

// Reglas de cálculo (según explicación operativa del negocio):
// - "Ciclos de máquina" = lectura diaria del CONTADOR físico de la lavadora
//   (contadorNuevo - contador del día anterior), tomada SOLO de los registros
//   NO abortados. El contador de la máquina avanza con el uso real del equipo
//   durante todo el día (incluido el tiempo de una carga que luego se abortó),
//   así que ya refleja esa actividad; por eso el registro de "abortado" solo
//   guarda el valor del contador como referencia, pero su campo `ciclos` NO se
//   puede sumar sobre el total de la máquina: sumarlo duplicaría esos ciclos
//   (el backend no filtra por `abortado` en los totales agregados, así que
//   `washing.ciclos` mezcla ambos — por eso no se usa aquí).
// - "Ciclos válidos" = "Ciclos de máquina" - "Abortados". Es el número que se
//   compara contra los lotes registrados para saber cuántos lotes faltan por
//   registrar (idealmente ciclosValidos === lotesCantidad).
// - Un ciclo abortado SIEMPRE pesa 0 kg reales (kilosLavados=0 al crearse), por lo
//   que los kilos nominales del periodo ya excluyen los abortados de forma natural.
// - "Día operado" = día con al menos un LOTE o un CICLO VÁLIDO (no abortado) en el
//   periodo (no día calendario). Los días donde lo único que hubo fue un ciclo
//   abortado NO cuentan como día operado, porque no representan producción real.
// - kg_dia = kg_periodo / dias_operados
// - Semáforo kilos: verde ≤ ±3%, ámbar 3-8%, rojo > 8%
const fmtPct = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: d });
const fmtKg = (n) => `${fmtNum(n, 1)} kg`;
const dayKey = (value) => (value ? String(value).slice(0, 10) : null);

const KpisCuadreSection = ({ kpisCuadre, daysInRange }) => {
  const { data, loading, error, reload } = kpisCuadre;

  const computed = useMemo(() => {
    if (!data) return null;
    const { reception, cycles = [], receptionDaily = [] } = data;

    // Lotes (recepción) — el total ya incluye finalizados + pendientes
    const lotesCantidad = Number(reception.conteo || 0);
    const lotesFinalizados = Number(reception.finalizados || 0);
    const lotesPendientes = Number(reception.pendientes || 0);
    const kilosRecepcion = Number(reception.kilosProcesados || 0);

    // Separamos ciclos válidos (basados en contador) de abortados (registro manual
    // de referencia) a partir del listado crudo del periodo.
    const ciclosValidosDocs = cycles.filter((c) => c?.abortado !== true);
    const ciclosAbortadosDocs = cycles.filter((c) => c?.abortado === true);

    // Ciclos de máquina = SOLO los ciclos basados en contador (no abortados).
    // No usamos el total agregado del backend (`washing.ciclos`) porque ese
    // suma también el campo `ciclos` de los registros abortados, duplicando
    // ciclos que el contador ya capturó.
    const ciclosDeMaquina = ciclosValidosDocs.reduce((s, c) => s + Number(c.ciclos || 0), 0);
    const kilosNominales = ciclosValidosDocs.reduce((s, c) => s + Number(c.kilosLavados || 0), 0);

    // Abortados: sumamos el campo `ciclos` de cada registro (un registro de
    // abortado puede representar más de un ciclo), no la cantidad de documentos.
    const ciclosAbortados = ciclosAbortadosDocs.reduce((s, c) => s + Number(c.ciclos || 0), 0);
    const kilosAbortados = ciclosAbortadosDocs.reduce(
      (s, c) => s + Number(c.ciclos || 0) * Number(c.capacidad || 0),
      0,
    );

    // Ciclos válidos = Ciclos de máquina - Abortados. Se usa para saber cuántos
    // lotes faltan por registrar (lotesCantidad debería igualar a este número).
    const ciclosValidos = Math.max(ciclosDeMaquina - ciclosAbortados, 0);

    // Kilos de "Ciclos válidos" = mismo criterio que el conteo: los kilos nominales
    // de la máquina menos los kilos (nominales) que representaban los abortados.
    // Si no se hace esta misma resta, la tarjeta de "Ciclos válidos" mostraría
    // menos ciclos que "Ciclos de máquina" pero los mismos kilos, lo cual no cuadra.
    const kilosNominalesValidos = Math.max(kilosNominales - kilosAbortados, 0);

    // Cuadre: ciclos válidos vs lotes (ambos ya netos de abortados)
    const diferenciaCantidad = ciclosValidos - lotesCantidad;
    const diferenciaKilos = kilosNominalesValidos - kilosRecepcion;
    const pctKilos = kilosRecepcion > 0 ? (diferenciaKilos / kilosRecepcion) * 100 : 0;

    // Días operados = día con al menos un lote O un ciclo válido (no abortado).
    // Un día donde solo hubo abortados no cuenta como día operado.
    const diasOperadosSet = new Set(receptionDaily.map((r) => dayKey(r?.date)));
    ciclosValidosDocs.forEach((c) => {
      const k = dayKey(c.fecha);
      if (k) diasOperadosSet.add(k);
    });
    const diasOperados = Math.max(diasOperadosSet.size, 1);

    // kg/día
    const kgPorDiaLotes = kilosRecepcion / diasOperados;
    const kgPorDiaCiclos = kilosNominales / diasOperados; // bruto, tarjeta "Ciclos de máquina"
    const kgPorDiaValidos = kilosNominalesValidos / diasOperados; // neto, tarjeta "Ciclos válidos"
    const kgPorDiaAbortados = kilosAbortados / diasOperados;

    // Promedio kg por carga
    const kgPromLote = lotesCantidad > 0 ? kilosRecepcion / lotesCantidad : 0;
    const kgPromCiclo = ciclosValidos > 0 ? kilosNominalesValidos / ciclosValidos : 0;

    // % abortados (sobre el universo ciclos de máquina + abortados)
    const pctAbortados = (ciclosDeMaquina + ciclosAbortados) > 0
      ? (ciclosAbortados / (ciclosDeMaquina + ciclosAbortados)) * 100
      : 0;

    // Semáforo cantidad
    const semaforoCantidad = diferenciaCantidad === 0 ? 'ok' : 'danger';
    // Semáforo kilos: verde ≤3%, ámbar 3-8%, rojo >8%
    const absPctKilos = Math.abs(pctKilos);
    const semaforoKilos = absPctKilos <= 3 ? 'ok' : absPctKilos <= 8 ? 'warn' : 'danger';

    // Sin lote = ciclos válidos que no tienen un lote asociado (diferencia positiva
    // entre ciclos válidos y lotes registrados).
    const sinLoteCiclos = Math.max(diferenciaCantidad, 0);

    return {
      lotesCantidad,
      lotesFinalizados,
      lotesPendientes,
      kilosRecepcion,
      ciclosDeMaquina,
      ciclosAbortados,
      ciclosValidos,
      kilosNominales,
      kilosNominalesValidos,
      kilosAbortados,
      diferenciaCantidad,
      diferenciaKilos,
      pctKilos,
      diasOperados,
      kgPorDiaLotes,
      kgPorDiaCiclos,
      kgPorDiaValidos,
      kgPorDiaAbortados,
      kgPromLote,
      kgPromCiclo,
      pctAbortados,
      semaforoCantidad,
      semaforoKilos,
      sinLoteCiclos,
    };
  }, [data]);

  if (!computed) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 p-5 h-32 animate-pulse" />
          ))}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 p-5 h-48 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* === Banner de alerta superior === */}
      {computed.sinLoteCiclos > 0 && (
        <div className="flex items-center gap-2.5 bg-[#FDF3E4] dark:bg-amber-900/20 px-4 py-2.5 rounded-[10px] border-l-4 border-[#E9A13B]">
          <AlertTriangle className="h-4 w-4 text-[#E9A13B] flex-shrink-0" />
          <div>
            <div className="text-[12.5px] font-semibold text-[#8A5A12] dark:text-amber-200">
              {computed.sinLoteCiclos} {computed.sinLoteCiclos === 1 ? 'ciclo sin lote asociado' : 'ciclos sin lote asociado'}
            </div>
            <div className="text-[11px] text-[#A87A32] dark:text-amber-300/80">
              Requieren revisión del operario
            </div>
          </div>
        </div>
      )}

      {/* === Días evaluados según el filtro === */}
      <div className="flex items-center gap-2 text-[11.5px] text-[#43474F] dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5 bg-[#F3F3F3] dark:bg-gray-700/40 px-2.5 py-1 rounded-full">
          <CalendarDays className="h-3 w-3" />
          {daysInRange ? `${fmtNum(daysInRange)} ${daysInRange === 1 ? 'día' : 'días'} del filtro` : 'Periodo seleccionado'}
        </span>
        <span className="inline-flex items-center gap-1.5 bg-[#F3F3F3] dark:bg-gray-700/40 px-2.5 py-1 rounded-full">
          {fmtNum(computed.diasOperados)} {computed.diasOperados === 1 ? 'día operado' : 'días operados'} (con lotes o ciclos válidos)
        </span>
      </div>

      {/* === KPIs (4 tarjetas) === */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<ClipboardList className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Lotes registrados"
          value={fmtNum(computed.lotesCantidad)}
          unit="lotes"
          sub={{ label: 'Kilos pesados', value: fmtKg(computed.kilosRecepcion) }}
          sub2={{ label: 'Kilos por día', value: fmtKg(computed.kgPorDiaLotes) }}
          extra={
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-[11.5px] text-[#43474F] dark:text-gray-400">Finalizados / Pendientes</span>
              <b className="text-[13.5px] font-semibold text-[#0F3460] dark:text-gray-200">
                {fmtNum(computed.lotesFinalizados)} / {fmtNum(computed.lotesPendientes)}
              </b>
            </div>
          }
        />
        <KpiCard
          icon={<Cog className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Ciclos de máquina"
          value={fmtNum(computed.ciclosDeMaquina)}
          unit="ciclos"
          sub={{ label: 'Kilos nominales', value: fmtKg(computed.kilosNominales) }}
          sub2={{ label: 'Kilos por día', value: fmtKg(computed.kgPorDiaCiclos) }}
        />
        <KpiCard
          icon={<XCircle className="h-3.5 w-3.5 text-[#B71D3F]" />}
          label="Ciclos abortados"
          value={fmtNum(computed.ciclosAbortados)}
          unit="ciclos"
          status={computed.ciclosAbortados > 0 ? 'danger' : 'ok'}
          showTag={false}
          sub={{ label: 'Kilos perdidos', value: fmtKg(computed.kilosAbortados) }}
          sub2={{ label: 'Kilos por día', value: fmtKg(computed.kgPorDiaAbortados) }}
        />
        <KpiCard
          icon={<CheckCircle2 className="h-3.5 w-3.5 text-[#0E9F6E]" />}
          label="Ciclos válidos"
          value={fmtNum(computed.ciclosValidos)}
          unit="ciclos"
          status={ 'ok'}
          sub={{ label: 'Kilos nominales', value: fmtKg(computed.kilosNominalesValidos) }}
          sub2={{ label: 'Kilos por día', value: fmtKg(computed.kgPorDiaValidos) }}
        />
      </div>

      {/* === Cuadre del periodo === */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-[#001F41] dark:text-white tracking-tight">
              Cuadre del periodo
            </h2>
            <p className="text-[11.5px] text-[#43474F] dark:text-gray-400 mt-0.5">
              La cantidad debe ser idéntica. Los kilos admiten variación (peso real vs. carga nominal).
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 pl-3.5 pb-2.5">Concepto</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2.5">Lotes (manual)</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2.5">Ciclos (máquina)</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2.5">Diferencia</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2.5">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-[#F3F3F3] dark:bg-gray-700/40">
                <td className="text-[13.5px] px-3.5 py-3 rounded-l-lg font-semibold text-[#1A1C1C] dark:text-white">
                  Cantidad de cargas
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 font-normal whitespace-nowrap">
                    Debe cuadrar en 0
                  </span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className="text-[16px] font-bold text-[#001F41] dark:text-white">{fmtNum(computed.lotesCantidad)}</span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">lotes creados</span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className="text-[16px] font-bold text-[#001F41] dark:text-white">{fmtNum(computed.ciclosValidos)}</span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">ciclos válidos</span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className={`text-[16px] font-bold ${computed.semaforoCantidad === 'ok' ? 'text-[#0E9F6E]' : 'text-[#B71D3F]'}`}>
                    {computed.diferenciaCantidad > 0 ? '+' : ''}{computed.diferenciaCantidad}
                  </span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">
                    {computed.lotesCantidad ? Math.abs((computed.diferenciaCantidad / computed.lotesCantidad) * 100).toFixed(2) : '0.00'}%
                  </span>
                </td>
                <td className="text-right px-3 py-3 rounded-r-lg">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${
                    computed.semaforoCantidad === 'ok' ? 'bg-[#E8F6F0] text-[#0E9F6E]' : 'bg-[#FEECEF] text-[#B71D3F]'
                  }`}>
                    ● {computed.semaforoCantidad === 'ok' ? 'Cuadra' : 'No cuadra'}
                  </span>
                </td>
              </tr>
              <tr><td colSpan={5} className="h-1.5" /></tr>
              <tr className="bg-[#FAFAFA] dark:bg-gray-700/20">
                <td className="text-[13.5px] px-3.5 py-3 rounded-l-lg font-semibold text-[#1A1C1C] dark:text-white">
                  Kilos procesados
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 font-normal whitespace-nowrap">
                    Tolerancia ± 3,0%
                  </span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className="text-[16px] font-bold text-[#001F41] dark:text-white">{fmtNum(computed.kilosRecepcion, 1)}</span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">kg pesados</span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className="text-[16px] font-bold text-[#001F41] dark:text-white">{fmtNum(computed.kilosNominalesValidos, 1)}</span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">kg nominales</span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className={`text-[16px] font-bold ${computed.semaforoKilos === 'ok' ? 'text-[#0E9F6E]' : 'text-[#B71D3F]'}`}>
                    {fmtPct(computed.pctKilos)}
                  </span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">
                    {fmtKg(Math.abs(computed.diferenciaKilos))}
                  </span>
                </td>
                <td className="text-right px-3 py-3 rounded-r-lg">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${
                    computed.semaforoKilos === 'ok' ? 'bg-[#E8F6F0] text-[#0E9F6E]'
                    : computed.semaforoKilos === 'warn' ? 'bg-[#FDF3E4] text-[#B87A18]'
                    : 'bg-[#FEECEF] text-[#B71D3F]'
                  }`}>
                    ● {computed.semaforoKilos === 'ok' ? 'En tolerancia' : computed.semaforoKilos === 'warn' ? 'En observación' : 'Fuera de rango'}
                  </span>
                </td>
              </tr>
              <tr><td colSpan={5} className="h-1.5" /></tr>
              <tr className="bg-[#F3F3F3] dark:bg-gray-700/40">
                <td className="text-[13.5px] px-3.5 py-3 rounded-l-lg font-semibold text-[#1A1C1C] dark:text-white">
                  Kg promedio por carga
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 font-normal whitespace-nowrap">
                    Indicador de llenado
                  </span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className="text-[16px] font-bold text-[#001F41] dark:text-white">{fmtNum(computed.kgPromLote, 1)}</span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">kg / lote</span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className="text-[16px] font-bold text-[#001F41] dark:text-white">{fmtNum(computed.kgPromCiclo, 1)}</span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">kg / ciclo</span>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <span className={`text-[16px] font-bold ${Math.abs(computed.kgPromCiclo - computed.kgPromLote) < 2 ? 'text-[#0E9F6E]' : 'text-[#B71D3F]'}`}>
                    {computed.kgPromCiclo > computed.kgPromLote ? '+' : ''}{(computed.kgPromCiclo - computed.kgPromLote).toFixed(1)}
                  </span>
                  <span className="block text-[11px] text-[#43474F] dark:text-gray-400 whitespace-nowrap">
                    {computed.kgPromLote > 0 ? fmtPct(((computed.kgPromCiclo - computed.kgPromLote) / computed.kgPromLote) * 100) : '0%'}
                  </span>
                </td>
                <td className="text-right px-3 py-3 rounded-r-lg">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#E8F6F0] text-[#0E9F6E]">
                    ● Indicador OK
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Trazabilidad: waterfall */}
        <div className="mt-5 bg-[#F3F3F3] dark:bg-gray-700/40 rounded-[10px] p-4">
          <div className="text-[11px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 mb-3.5">
            Trazabilidad: de ciclos totales a lotes conciliados
          </div>
          <div className="flex h-9 rounded-lg overflow-hidden mb-2.5">
            {computed.ciclosValidos > 0 && (
              <div
                className="bg-[#0F3460] flex items-center justify-center text-white text-[12px] font-bold"
                style={{ flex: computed.ciclosValidos }}
              >
                {computed.ciclosValidos} conciliados
              </div>
            )}
            {computed.ciclosAbortados > 0 && (
              <div
                className="bg-[#FC536D] flex items-center justify-center text-white text-[12px] font-bold"
                style={{ flex: Math.max(computed.ciclosAbortados, 0.5) }}
              >
                {computed.ciclosAbortados}
              </div>
            )}
            {computed.sinLoteCiclos > 0 && (
              <div
                className="bg-[#E9A13B] flex items-center justify-center text-white text-[12px] font-bold min-w-[44px]"
                style={{ flex: Math.max(computed.sinLoteCiclos, 0.5) }}
              >
                {computed.sinLoteCiclos}
              </div>
            )}
          </div>
          <div className="flex gap-5 flex-wrap text-[11.5px] text-[#43474F] dark:text-gray-400">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#0F3460]" />
              Ciclos con lote asociado · <b className="text-[#1A1C1C] dark:text-white font-bold">{computed.ciclosValidos}</b>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#FC536D]" />
              Abortados · <b className="text-[#1A1C1C] dark:text-white font-bold">{computed.ciclosAbortados}</b>
            </div>
            {computed.sinLoteCiclos > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#E9A13B]" />
                Sin lote asociado · <b className="text-[#1A1C1C] dark:text-white font-bold">{computed.sinLoteCiclos}</b>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KpisCuadreSection;
